import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Wordmark from "../Brand/Wordmark";

/*
 * Shared public-site header.
 *
 * Extracted verbatim from the hand-rolled copies that used to live inline
 * in every marketing/legal page (LandingPage, About, Features, HelpCenter,
 * Status, Security, Contact, Jobs/JobsList, Jobs/JobDetail,
 * Legal/PrivacyPolicy, Legal/TermsAndConditions) — this is a pure
 * extraction, not a redesign.
 *
 * `full`  — logo + nav (Features, Security, About, Contact, Browse jobs)
 *           + Sign in + Start hiring CTA. Markup taken from LandingPage's
 *           former inline header.
 * `slim`  — logo + "Back to home" link. Markup taken from the legal pages'
 *           former inline header.
 *
 * Note: the previous per-page copies each hand-rolled their own bespoke
 * "active nav item" styling (a saffron color override, an underline, or a
 * text-ink swap — no two pages did it the same way). None of that is
 * reproduced here since the shared component has no notion of which page
 * it's rendered on; the nav now renders identically on every page.
 */

function FullNav(): JSX.Element {
  return (
    <header className="border-b border-hairline bg-cream sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-8 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          aria-label="Velaigal home"
          className="text-ink no-underline"
          style={{ textDecoration: "none" }}
        >
          <Wordmark variant="lockup" size="md" animateOnMount />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/features" className="velaigal-link text-sm font-medium" style={{ textDecoration: "none" }}>
            Features
          </Link>
          <Link to="/security" className="velaigal-link text-sm font-medium" style={{ textDecoration: "none" }}>
            Security
          </Link>
          <Link to="/about" className="velaigal-link text-sm font-medium" style={{ textDecoration: "none" }}>
            About
          </Link>
          <Link to="/contact" className="velaigal-link text-sm font-medium" style={{ textDecoration: "none" }}>
            Contact
          </Link>
          <Link to="/jobs" className="velaigal-link text-sm font-medium" style={{ textDecoration: "none" }}>
            Browse jobs
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="hidden sm:inline-flex velaigal-link text-sm font-medium"
            style={{ textDecoration: "none" }}
          >
            Sign in
          </Link>
          <Link
            to="/signup/hr"
            className="btn-primary no-underline"
            style={{ textDecoration: "none" }}
          >
            Start hiring
          </Link>
        </div>
      </div>
    </header>
  );
}

function SlimNav(): JSX.Element {
  return (
    <header className="border-b border-hairline bg-cream sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-8 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          aria-label="Velaigal home"
          className="text-ink no-underline"
          style={{ textDecoration: "none" }}
        >
          <Wordmark variant="lockup" size="md" />
        </Link>
        <Link
          to="/"
          className="velaigal-link text-sm font-medium inline-flex items-center"
          style={{ textDecoration: "none" }}
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
          Back to home
        </Link>
      </div>
    </header>
  );
}

export function PublicHeader({ variant = "full" }: { variant?: "full" | "slim" }): JSX.Element {
  return variant === "slim" ? <SlimNav /> : <FullNav />;
}

export default PublicHeader;
